import { useState } from "react";
import { Copy, ExternalLink, Link2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { SubBadge } from "./SectionShell";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { SectionCard } from "./SectionShell";
import { CheckinCells, WatchCells } from "./ClassroomSection";
import { openWhatsApp } from "@/lib/whatsapp";

const guideFormUrl = (token) => `${window.location.origin}/guide-info/${token}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const LinkPanel = ({ gi, batchNo }) => {
  const url = gi.link?.token ? guideFormUrl(gi.link.token) : "";
  const share = `🙏 Namaste, please fill in your Guides Information for the Positive Life program (Batch ${batchNo || ""}): ${url}`;
  return (
    <div className="rounded-xl border border-border bg-white/60 p-4 mb-5" data-testid="guide-info-link-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium flex items-center gap-2"><SubBadge /><Link2 size={16} className="text-accent" /> Guides Information form link</p>
          <p className="text-xs text-muted-foreground mt-0.5">Send this link to the Guides. Each guide fills the form once and appears in the table below.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Link open</span>
          <Switch data-testid="guide-info-link-enabled" checked={gi.link?.enabled !== false} onCheckedChange={gi.toggleLink} disabled={!url} />
          <button data-testid="guide-info-create-link" onClick={gi.createLink} disabled={gi.busy} className="inline-flex items-center gap-1.5 rounded-full bg-[#1A2E26] text-white px-4 py-2 text-xs font-medium hover:opacity-90 disabled:opacity-50">
            <RefreshCw size={12} /> {url ? "Create new link" : "Create link"}
          </button>
        </div>
      </div>
      {url && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <code data-testid="guide-info-link-url" className="text-xs bg-muted/60 rounded-lg px-3 py-2 break-all flex-1 min-w-[240px]">{url}</code>
          <button data-testid="guide-info-copy-link" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-xs hover:bg-muted"><Copy size={12} /> Copy</button>
          <button data-testid="guide-info-wa-share" onClick={() => openWhatsApp("", share)} className="inline-flex items-center gap-1.5 rounded-full border border-[#128C7E] text-[#128C7E] px-4 py-2 text-xs hover:bg-[#128C7E] hover:text-white transition-colors"><ExternalLink size={12} /> Share on WhatsApp</button>
          <a href={url} target="_blank" rel="noreferrer" data-testid="guide-info-open-link" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-xs hover:bg-muted"><ExternalLink size={12} /> Open form</a>
        </div>
      )}
    </div>
  );
};

const EMPTY = { name: "", mobile: "", whatsapp: "", email: "", profession: "", place: "" };
const AddGuide = ({ gi }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await gi.add(f); toast.success(`${f.name} added to Guides`); setF(EMPTY); setOpen(false); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  if (!open) return <button type="button" data-testid="guide-add-record-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-[#1A2E26] text-white px-4 py-2 text-xs font-medium hover:bg-[#234034]"><Plus size={13} /> Add record</button>;
  return (
    <form onSubmit={submit} data-testid="guide-add-form" className="w-full rounded-lg border border-border bg-white p-3 grid gap-2 sm:grid-cols-3">
      {[["name", "Name *"], ["mobile", "Mobile *"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["profession", "Profession"], ["place", "Place"]].map(([k, l]) => (
        <input key={k} data-testid={`guide-add-${k}`} value={f[k]} onChange={set(k)} placeholder={l} required={l.endsWith("*")} className="bg-white border border-border rounded-lg px-3 py-1.5 text-sm" />
      ))}
      <div className="sm:col-span-3 flex gap-2">
        <button data-testid="guide-add-save" disabled={busy} className="rounded-full bg-[#1A2E26] text-white px-4 py-1.5 text-xs font-medium disabled:opacity-50">{busy ? "Saving…" : "Save guide"}</button>
        <button type="button" data-testid="guide-add-cancel" onClick={() => setOpen(false)} className="rounded-full border border-border px-4 py-1.5 text-xs">Cancel</button>
      </div>
    </form>
  );
};

const COLS = ["#", "Name", "Mobile", "WhatsApp", "Email", "Profession", "Place", "Batch", "Watch time (Day 1–6)", "Daily check (D2–D6)", "Filled on", ""];

export const GuidesInfoSection = ({ gi, batchNo, questions = [] }) => (
  <SectionCard id="guide-info" testId="section-guide-info">
    <LinkPanel gi={gi} batchNo={batchNo} />
    <div className="rounded-xl border border-border bg-white/60 p-4" data-testid="guide-info-table">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <p className="font-medium flex items-center"><SubBadge /> Guides ({gi.guides.length})</p>
        <AddGuide gi={gi} />
      </div>
      {gi.guides.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="guide-info-empty">No guide has filled the form yet.</p>
      ) : (
        <div className="overflow-auto max-h-[420px]">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground"><tr>{COLS.map((h, i) => <th key={i} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>
              {gi.guides.map((g, i) => (
                <tr key={g.id} data-testid={`guide-info-row-${i}`} className="border-t border-border/60">
                  <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2 font-medium">{g.name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{g.mobile}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{g.whatsapp}</td>
                  <td className="px-3 py-2">{g.email || "—"}</td>
                  <td className="px-3 py-2">{g.profession}</td>
                  <td className="px-3 py-2">{g.place}</td>
                  <td className="px-3 py-2">{g.batch || "—"}</td>
                  <td className="px-3 py-2" data-testid={`guide-watched-${i}`}><WatchCells p={g} /></td>
                  <td className="px-3 py-2" data-testid={`guide-checkin-${i}`}><CheckinCells p={g} questions={questions} /></td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{fmtDate(g.created_at)}</td>
                  <td className="px-3 py-2">
                    <button data-testid={`guide-info-delete-${i}`} onClick={() => gi.remove(g)} title="Remove guide" className="grid place-items-center w-8 h-8 rounded-full border border-destructive/40 text-destructive hover:bg-destructive hover:text-white transition-colors"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </SectionCard>
);
